import { useEffect, useState, type DragEvent, type ReactNode } from "react";
import { Button as AriaButton, Menu, MenuItem, MenuTrigger, type Key } from "react-aria-components";
import { CalendarDays, Check, ChevronDown, Clock3, Copy, Download, Loader2, Plus, X } from "lucide-react";
import { queue } from "@/components/Toast";
import { cn } from "@/utils/helpers";
import { KitButton } from "../../components/kit/Button";
import { KitModal } from "../../components/kit/Modal";
import { KitPopover } from "../../components/kit/Popover";
import PlatformGlyph from "../../components/PlatformGlyph";
import type { PostViewport } from "../components/StatusBanner";
import LiveCoverDialog, { type CoverChoice } from "./LiveCoverDialog";
import { PLATFORM_NAME, channelName, clockLabel, dayLabel, durationLabel, fileForChannel } from "./liveModel";
import type { LiveStageHandle } from "./liveStage";
import type { LiveChannel, LiveFile, LivePost, LiveRoute } from "./liveTypes";

/**
 * The Live tab before the posts go out (Figma 08, 6 Live, "one pile"): every
 * file for the post in one pile on the left, each saying which channels it goes
 * to; How it posts (Schedule, Post now, Post it yourself) and Where it posts
 * (a row per channel with its file, cover and way out) on the right; the
 * captions in one row under both. The banner above says whose turn it is.
 */

const LABEL = "font-section text-[13px] font-semibold uppercase leading-[19px] tracking-[0.08em] text-co-muted";
const LINK = "text-co-accent-ink outline-none hover:underline focus-visible:underline disabled:opacity-50";
/** A full-width 48 button on the phone (Save the video, Copy caption). */
const PHONE_WIDE =
  "inline-flex h-12 w-full items-center justify-center gap-2 rounded-pill bg-co-tonal text-[14px] font-medium text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)] outline-none hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-co-accent";
const DESK_PILL =
  "inline-flex h-9 shrink-0 items-center gap-2 rounded-pill bg-co-tonal px-3.5 text-[13px] font-medium text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)] outline-none hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-co-accent";

function sizeLabel(bytes: number | null | undefined): string | null {
  if (!bytes) return null;
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return bytes >= 1024 * 1024 ? `${Math.round(bytes / (1024 * 1024))} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function ratioValue(ratio: string | null | undefined): number {
  const [w, h] = (ratio ?? "9:16").split(":").map(Number);
  return w && h ? w / h : 9 / 16;
}

const platformWord = (platform: string) => PLATFORM_NAME[platform] ?? platform;

/** "Takes a 9:16 video", "Takes a 4:5 video or pictures". */
function takesLine(channel: LiveChannel): string {
  if (channel.kind === "CAROUSEL") return `Takes ${channel.slides?.length ?? "several"} pictures at ${channel.ratio}`;
  if (channel.kind === "IMAGE") return `Takes a ${channel.ratio} picture`;
  return `Takes a ${channel.ratio} video`;
}

/** A platform's mark in its 26px chip (Media chip / Platform): on, or dimmed off. */
function PlatformChip({
  platform,
  on = true,
  label,
  onPress,
  disabledReason,
}: {
  platform: string;
  on?: boolean;
  label?: string;
  onPress?: () => void;
  disabledReason?: string;
}) {
  const body = (
    <PlatformGlyph platform={platform} className={cn("size-3.5", on ? "text-co-text" : "text-co-muted opacity-60")} />
  );
  const box = "grid size-[26px] shrink-0 place-items-center rounded-[6px] bg-[color-mix(in_srgb,var(--co-ground)_40%,transparent)] shadow-[inset_0_0_0_1px_var(--co-line)]";
  if (!label) return <span className={box}>{body}</span>;
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      title={disabledReason ?? label}
      disabled={!onPress}
      onClick={onPress}
      className={cn(box, "outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent", onPress ? "hover:bg-co-hover" : "cursor-default")}
    >
      {body}
    </button>
  );
}

/**
 * The file's still at its own shape on a 128 (or 48) high box; or, with `box`
 * (the phone's 64 × 80 tile, Figma 08), inside a fixed dark tile with the file
 * at its real shape centred in it, grey either side.
 */
function FileStill({ file, height, box, className }: { file: LiveFile | null; height: number; box?: [number, number]; className?: string }) {
  const ratio = ratioValue(file?.ratio);
  const still = file?.thumbUrl ?? (file?.kind === "IMAGE" ? file.mediaUrl : null);
  const media = still ? (
    <img src={still} alt="" className="size-full object-cover" />
  ) : file?.kind === "VIDEO" && file.mediaUrl ? (
    <video src={`${file.mediaUrl}#t=0.1`} muted playsInline preload="metadata" className="size-full object-cover" />
  ) : null;
  if (box) {
    const inner = { width: Math.min(box[0], Math.round(box[1] * ratio)), height: Math.min(box[1], Math.round(box[0] / ratio)) };
    return (
      <span className={cn("grid shrink-0 place-items-center overflow-hidden bg-co-recess", className)} style={{ width: box[0], height: box[1] }}>
        <span className="block overflow-hidden" style={inner}>
          {media}
        </span>
      </span>
    );
  }
  const width = Math.round(Math.min(Math.max(height * ratio, height * 0.5625), height * 1.78));
  return (
    <span className={cn("block shrink-0 overflow-hidden bg-co-recess", className)} style={{ width, height }}>
      {media}
    </span>
  );
}

/** How it posts: the post's one switch (Figma 08, x50000). */
type HowMode = "schedule" | "now" | "self";

/** "Thu 18:00" for a channel's route, or the post's planned time. */
function whenOf(route: LiveRoute | null | undefined, fallback: string | null, timeZone: string): string | null {
  const at = route?.at ?? fallback;
  return at ? `${dayLabel(at, timeZone).split(" ")[0]} ${clockLabel(at, timeZone)}` : null;
}

export default function LiveWorkspace({
  stage,
  viewport,
  clientName,
  canEdit,
  below,
}: {
  stage: LiveStageHandle;
  viewport: PostViewport;
  clientName: string;
  canEdit: boolean;
  /** What sits under the files on a static post (its pictures, numbered). */
  below?: ReactNode;
}) {
  const { data, timeZone } = stage;
  const phone = viewport === "phone";
  const desk = viewport === "desktop";
  const [dropping, setDropping] = useState(false);
  const [coverFor, setCoverFor] = useState<string | null>(null);
  const [postNowAsk, setPostNowAsk] = useState(false);
  const [linkFor, setLinkFor] = useState<string | null>(null);
  const [editingCaption, setEditingCaption] = useState<{ channelId: string; text: string } | null>(null);
  // Copy caption: the button reads Copied with a check for a moment, and a toast names the channel (Figma 08 phone).
  const [copiedFor, setCopiedFor] = useState<string | null>(null);
  const copyCaption = (channel: LiveChannel, caption: string) => {
    void navigator.clipboard.writeText(caption).then(
      () => {
        setCopiedFor(channel.id);
        queue.add({ title: `${channelName(channel)} caption copied`, variant: "success" });
        window.setTimeout(() => setCopiedFor((current) => (current === channel.id ? null : current)), 2500);
      },
      () => queue.add({ title: "Could not copy the caption", variant: "error" }),
    );
  };
  // A post-it-yourself time coming round opens its panel: the clock moves every half minute.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const files = data.files.filter((file) => !file.id.endsWith(":cover"));
  const byId = new Map(data.files.map((file) => [file.id, file]));
  const pictures = files.filter((file) => file.kind === "IMAGE" && file.status === "ready");
  const postOf = (channelId: string): LivePost | undefined => data.posts.find((post) => post.channelId === channelId);
  const mainOf = (channelId: string) => fileForChannel(data, channelId);
  // A cover picture; TikTok never has one (its cover is a frame of the video).
  const coverOf = (channelId: string) =>
    data.channels.find((channel) => channel.id === channelId)?.platform === "TIKTOK"
      ? null
      : (data.matches.find((match) => match.role === "cover" && match.channelIds.includes(channelId) && !match.fileId.endsWith(":cover"))?.fileId ?? null);
  const platforms = [...new Set(data.channels.map((channel) => channel.platform))];
  const count = data.channels.length;
  const countWord = count === 1 ? "the channel" : count === 2 ? "both channels" : `all ${["", "one", "two", "three", "four", "five", "six", "seven", "eight"][count] ?? count} channels`;
  const city = data.timezoneCity ?? (timeZone.includes("/") ? timeZone.split("/").pop()!.replace(/_/g, " ") : timeZone);
  const live = data.posts.filter((post) => post.liveSince);
  const pending = data.posts.filter((post) => !post.liveSince);
  const editableRoutes = pending.filter((post) => post.route?.editable && post.route.at);
  const selfPosts = pending.filter((post) => post.route?.state === "manual");
  const scheduled = pending.filter((post) => post.route?.state === "scheduled");
  const posting = pending.some((post) => post.route?.state === "posting");
  const plannedAt = data.goesOutAt ?? editableRoutes[0]?.route?.at ?? null;
  const savedMode: HowMode = posting ? "now" : pending.length > 0 && selfPosts.length === pending.length ? "self" : "schedule";
  // A switch on its way: the picked way shows at once, with a spinner, and the
  // switch holds still until every channel has taken it (each channel is its
  // own request, then a fresh read). A refusal falls back to what was saved.
  const [switchingTo, setSwitchingTo] = useState<HowMode | null>(null);
  const mode: HowMode = switchingTo ?? savedMode;
  /** A channel row's own change on its way: what the row is becoming, by channel. */
  const [rowPending, setRowPending] = useState<Record<string, string>>({});

  // ------------------------------------------------------------ moves
  const assign = (channel: LiveChannel, file: LiveFile) => {
    if (!stage.swapFile) return;
    void Promise.resolve(stage.swapFile(channel.id, file.id)).catch(() => undefined);
  };
  const setMode = (next: HowMode) => {
    if (next === mode || switchingTo || !canEdit) return;
    if (next === "now") {
      setPostNowAsk(true);
      return;
    }
    if (!stage.setSelfPost) return;
    const targets = next === "self" ? pending.filter((post) => post.publicationId && post.route?.state !== "manual") : selfPosts;
    if (!targets.length) return;
    setSwitchingTo(next);
    void (async () => {
      let changed = 0;
      for (const post of targets) {
        await Promise.resolve(stage.setSelfPost!(post.channelId, next === "self")).then(
          () => changed++,
          () => undefined,
        );
      }
      setSwitchingTo(null);
      if (changed) {
        queue.add({
          title: next === "self" ? "You post it yourself" : "Creo posts it",
          description: next === "self" ? "At its time the bell has the file and caption ready." : "Schedule it when it is ready.",
          variant: "success",
        });
      }
    })();
  };
  const postEveryNow = (targets: LivePost[]) => {
    setSwitchingTo("now");
    void (async () => {
      for (const post of targets) await Promise.resolve(stage.postNow?.(post.channelId)).catch(() => undefined);
      setSwitchingTo(null);
    })();
  };
  const setEvery = async (day: string, time: string) => {
    if (!stage.setTime) return;
    for (const post of editableRoutes) await Promise.resolve(stage.setTime(post.channelId, day, time)).catch(() => undefined);
  };
  const confirmCover = (channel: LiveChannel, choice: CoverChoice) => {
    const before = { ms: postOf(channel.id)?.coverTimestampMs ?? null, file: coverOf(channel.id) };
    void (async () => {
      try {
        if (choice.kind === "frame") {
          if (before.file && stage.setCover) await stage.setCover(channel.id, null);
          await stage.setCoverMoment?.(channel.id, choice.ms);
        } else if (choice.kind === "auto") {
          // Let TikTok choose: no frame set, TikTok picks one when it posts.
          await stage.setCoverMoment?.(channel.id, null);
        } else {
          await stage.setCover?.(channel.id, choice.fileId);
        }
        queue.add({
          title: choice.kind === "auto" ? `${channelName(channel)} picks the cover` : `Cover set for ${channelName(channel)}`,
          variant: "success",
          action: {
            label: "Undo",
            onPress: () =>
              void (async () => {
                if (stage.setCover) await stage.setCover(channel.id, before.file);
                if (channel.platform !== "YOUTUBE") await stage.setCoverMoment?.(channel.id, before.ms);
              })().catch(() => undefined),
          },
        });
      } catch {
        // The stage's toast says why.
      }
    })();
  };

  // ------------------------------------------------------------ drop
  const canDrop = canEdit && !!stage.addFiles;
  const onDrag = (event: DragEvent) => {
    if (!canDrop || !Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    setDropping(true);
  };
  const dropZone = {
    onDragOver: onDrag,
    onDragEnter: onDrag,
    onDragLeave: (event: DragEvent) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      setDropping(false);
    },
    onDrop: (event: DragEvent) => {
      if (!canDrop) return;
      event.preventDefault();
      setDropping(false);
      const dropped = Array.from(event.dataTransfer.files ?? []);
      if (dropped.length) stage.addFiles?.(dropped);
    },
  };

  // ------------------------------------------------------------ files
  const coverLine = (channel: LiveChannel): string | null => {
    if (channel.kind !== "VIDEO" || !channel.wantsCover) return null;
    const picture = coverOf(channel.id);
    const ms = postOf(channel.id)?.coverTimestampMs ?? null;
    if (picture) return `Cover: ${byId.get(picture)?.name ?? "a picture"}`;
    if (ms !== null) return `Cover: frame at ${durationLabel(Math.round(ms / 1000)) ?? "0:00"}`;
    return channel.platform === "TIKTOK" ? "Cover: TikTok picks a frame" : "Cover: the first frame";
  };
  const canChangeCover = (channel: LiveChannel) => {
    const post = postOf(channel.id);
    if (!canEdit || channel.kind !== "VIDEO" || !channel.wantsCover || post?.liveSince) return false;
    if (post?.route?.state === "scheduled" || post?.route?.state === "posting") return false;
    return channel.platform === "YOUTUBE" ? !!stage.setCover : !!stage.setCoverMoment;
  };

  const fileCard = (file: LiveFile) => {
    const goes = data.channels.filter((channel) => mainOf(channel.id) === file.id);
    const covers = data.channels.filter((channel) => coverOf(channel.id) === file.id);
    const uploading = file.status === "uploading" || file.status === "processing";
    const coverChannel = goes.find((channel) => channel.platform === "TIKTOK" && channel.wantsCover) ?? goes.find((channel) => channel.wantsCover);
    const meta = uploading
      ? file.status === "uploading"
        ? `Uploading${typeof file.progress === "number" ? `, ${file.progress}%` : ""}`
        : "Saving"
      : [file.kind === "VIDEO" ? "Video" : "Picture", file.ratio, file.kind === "VIDEO" ? durationLabel(file.durationSeconds) : null, sizeLabel(file.sizeBytes)]
          .filter(Boolean)
          .join(" · ");
    return (
      <li key={file.id} className="relative flex gap-3.5 rounded-card bg-co-surface p-3">
        <FileStill file={file} height={128} className="rounded-[8px]" />
        <div className="flex min-w-0 flex-1 flex-col gap-1 pr-6">
          <p className="truncate text-[13.5px] font-semibold leading-5 text-co-text2">{file.name}</p>
          <p className={cn("text-[12.5px] leading-[18px]", file.status === "failed" ? "text-co-danger" : "text-co-muted")}>
            {file.status === "failed" ? (file.error ?? "It did not upload. Remove it and add it again.") : meta}
          </p>
          {uploading && typeof file.progress === "number" ? (
            <span className="mt-1 block h-1 w-full max-w-[240px] overflow-hidden rounded-pill bg-co-tonal" aria-hidden="true">
              <span className="block h-full bg-co-accent transition-[width]" style={{ width: `${file.progress}%` }} />
            </span>
          ) : null}
          {!uploading && file.status !== "failed" ? (
            <>
              <div className="flex items-center gap-1.5 pt-2">
                <span className="mr-0.5 text-[12.5px] text-co-muted">Goes to</span>
                {platforms.map((platform) => {
                  const on = goes.some((channel) => channel.platform === platform);
                  const fits = data.channels.find(
                    (channel) =>
                      channel.platform === platform &&
                      (file.kind === "VIDEO" ? channel.kind === "VIDEO" : channel.kind !== "VIDEO") &&
                      mainOf(channel.id) !== file.id,
                  );
                  const locked = fits ? ["scheduled", "posting"].includes(postOf(fits.id)?.route?.state ?? "") || !!postOf(fits.id)?.liveSince : false;
                  return (
                    <PlatformChip
                      key={platform}
                      platform={platform}
                      on={on}
                      label={on ? `Goes to ${platformWord(platform)}` : `Send to ${platformWord(platform)}`}
                      onPress={!on && fits && !locked && canEdit && stage.swapFile ? () => assign(fits, file) : undefined}
                      disabledReason={
                        on
                          ? `${platformWord(platform)} posts this file. Give it another file to take it off this one.`
                          : !fits
                            ? `${platformWord(platform)} does not take a ${file.kind === "VIDEO" ? "video" : "picture"} on this post.`
                            : locked
                              ? `${platformWord(platform)} is scheduled or live.`
                              : undefined
                      }
                    />
                  );
                })}
              </div>
              <p className={cn("text-[12.5px] font-medium leading-[18px]", goes.length ? "text-co-text2" : "text-co-muted")}>
                {goes.length
                  ? goes.map(channelName).join(" · ")
                  : covers.length
                    ? `The cover of the ${covers.map(channelName).join(" and ")}`
                    : "Not placed. Tap a logo to use it there, or remove it."}
              </p>
              {coverChannel ? (
                <p className="text-[12.5px] font-medium leading-[18px] text-co-text2">
                  {coverLine(coverChannel)}
                  {canChangeCover(coverChannel) ? (
                    <>
                      {" · "}
                      <button type="button" onClick={() => setCoverFor(coverChannel.id)} className={LINK}>
                        Change
                      </button>
                    </>
                  ) : null}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
        {canEdit && stage.removeFile && stage.canRemove(file.id) ? (
          <button
            type="button"
            aria-label={`Remove ${file.name}`}
            onClick={() => stage.removeFile!(file.id)}
            className="absolute right-3 top-3 grid size-5 place-items-center text-co-muted outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            <X className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
          </button>
        ) : null}
      </li>
    );
  };

  const filesSection = (
    <section aria-labelledby="live-files" className="flex min-w-0 flex-col gap-3">
      <div className="flex h-8 items-center justify-between gap-3">
        <h2 id="live-files" className={LABEL}>
          Files · {files.length ? `${files.length} in one pile` : "none yet"}
        </h2>
        {files.length && canEdit && stage.addFiles ? (
          <button type="button" onClick={stage.openPicker} className="inline-flex items-center gap-1.5 px-1 text-[13px] font-medium text-co-muted outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent">
            <Plus className="size-3.5" aria-hidden="true" />
            Add files
          </button>
        ) : null}
      </div>
      <p className="text-[13px] leading-[19px] text-co-muted">
        {files.length
          ? "Every file for this post sits here, once. Creo sends each one to the channels its shape fits. Tap a logo to add a channel."
          : "Every file for this post goes in one pile. Creo sends each one to the channels its shape fits: a tall video to TikTok, Instagram Reels and YouTube Shorts, a wide video to YouTube, pictures to Instagram and the Facebook feed."}
      </p>
      {files.length ? (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">{files.map(fileCard)}</ul>
      ) : canEdit && stage.addFiles ? (
        <button
          type="button"
          onClick={stage.openPicker}
          className={cn(
            "flex h-[300px] flex-col items-center justify-center gap-2 rounded-card border border-dashed text-[13.5px] text-co-text2 outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent",
            dropping ? "border-co-accent bg-co-accent-dim" : "border-co-faint hover:border-co-text2",
          )}
        >
          <Plus className="size-4" strokeWidth={2} aria-hidden="true" />
          {phone ? "Add the files, all at once" : "Drop the files here, all at once"}
        </button>
      ) : (
        <p className="rounded-card bg-co-surface px-4 py-6 text-[13.5px] text-co-muted">No files yet.</p>
      )}
      {below}
    </section>
  );

  // ------------------------------------------------------------ how it posts
  const dayValue = plannedAt
    ? new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(plannedAt))
    : "";
  const timeValue = plannedAt ? clockLabel(plannedAt, timeZone) : "";
  const timesEditable = canEdit && !!stage.setTime && editableRoutes.length > 0 && scheduled.length === 0;
  const howNote = (() => {
    if (!files.length) return "Schedule it once the files are in. The time comes from the plan.";
    if (live.length && pending.length) {
      const self = selfPosts.map((post) => channelName(data.channels.find((channel) => channel.id === post.channelId)!));
      const failed = pending.filter((post) => post.route?.state === "failed").map((post) => channelName(data.channels.find((channel) => channel.id === post.channelId)!));
      const count = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight"][live.length] ?? String(live.length);
      return `${count} ${live.length === 1 ? "channel" : "channels"} went out${plannedAt ? ` at ${timeValue}` : ""}.${
        failed.length ? ` ${failed.join(" and ")} didn’t post; Creo tries again on its own.` : ""
      }${self.length ? ` ${self.join(" and ")} ${self.length === 1 ? "is" : "are"} set to post it yourself, on ${self.length === 1 ? "its" : "their"} row.` : ""}`;
    }
    if (mode === "self") return `You post ${countWord} yourself at this time. The bell has each file and caption ready.`;
    if (scheduled.length && scheduled.length === pending.length) return null;
    return `${countWord.charAt(0).toUpperCase()}${countWord.slice(1)} go out at this time. To post one channel another way, change it on its row.`;
  })();

  const howSection = (
    <section aria-labelledby="live-how" className="flex min-w-0 flex-col gap-3">
      <h2 id="live-how" className={LABEL}>
        How it posts
      </h2>
      <div className="flex flex-col gap-3.5 rounded-card bg-co-surface p-4">
        <div
          role="radiogroup"
          aria-label="How it posts"
          aria-busy={!!switchingTo}
          className="flex h-10 w-fit max-w-full rounded-pill bg-co-tonal p-[3px]"
        >
          {(
            [
              ["schedule", "Schedule"],
              ["now", "Post now"],
              ["self", "Post it yourself"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={mode === value}
              disabled={
                !canEdit ||
                !!switchingTo ||
                Object.keys(rowPending).length > 0 ||
                (value !== mode && (value === "now" ? !stage.postNow || !files.length : !stage.setSelfPost || !files.length))
              }
              onClick={() => setMode(value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-pill px-3 text-[13px] outline-none transition-colors focus-visible:outline-2 focus-visible:outline-co-accent disabled:cursor-default",
                mode === value ? "bg-co-hover font-semibold text-co-text shadow-[inset_0_0_0_1px_var(--co-line2)]" : "font-medium text-co-muted hover:text-co-text disabled:hover:text-co-muted",
                switchingTo && mode !== value && "opacity-60",
              )}
            >
              {label}
              {switchingTo === value ? <Loader2 className="size-3.5 animate-spin text-co-muted" aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative inline-flex h-[37px] items-center gap-2 rounded-card border border-co-line2 bg-co-recess px-3 text-[13px] font-medium text-co-text2">
            <CalendarDays className="size-4 text-co-muted" strokeWidth={1.7} aria-hidden="true" />
            {plannedAt ? dayLabel(plannedAt, timeZone) : "No day yet"}
            {timesEditable ? (
              <input
                type="date"
                aria-label="The day it goes out"
                value={dayValue}
                onChange={(event) => event.target.value && void setEvery(event.target.value, timeValue)}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
            ) : null}
          </label>
          <label className="relative inline-flex h-[37px] items-center gap-2 rounded-card border border-co-line2 bg-co-recess px-3 text-[13px] font-medium text-co-text2">
            <Clock3 className="size-4 text-co-muted" strokeWidth={1.7} aria-hidden="true" />
            {timeValue || "No time yet"}
            {timesEditable ? (
              <input
                type="time"
                aria-label="The time it goes out"
                value={timeValue}
                step={300}
                onChange={(event) => event.target.value && dayValue && void setEvery(dayValue, event.target.value)}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
            ) : null}
          </label>
          <span className="text-[12.5px] text-co-muted">From the plan · {city} time</span>
        </div>
        {scheduled.length && scheduled.length === pending.length ? (
          <p className="text-[12.5px] leading-[18px] text-co-text2">
            Scheduled · Creo posts {countWord} at this time.{" "}
            {canEdit && stage.unschedule ? (
              <button
                type="button"
                onClick={() => void Promise.all(scheduled.map((post) => Promise.resolve(stage.unschedule!(post.channelId)).catch(() => undefined)))}
                className={LINK}
              >
                Unschedule
              </button>
            ) : null}
          </p>
        ) : howNote ? (
          <p className="text-[12.5px] leading-[18px] text-co-muted">{howNote}</p>
        ) : null}
      </div>
    </section>
  );

  // ------------------------------------------------------------ where it posts
  const howLabel = (post: LivePost | undefined): { text: string; tone: "plain" | "accent" | "alert" | "live" } => {
    if (post?.liveSince) return { text: `Live since ${clockLabel(post.liveSince, timeZone)}`, tone: "live" };
    const route = post?.route;
    const when = whenOf(route, plannedAt, timeZone);
    switch (route?.state) {
      case "scheduled":
        return { text: when ? `Scheduled · ${when}` : "Scheduled", tone: "plain" };
      case "manual":
        return { text: "Post it yourself", tone: "accent" };
      case "posting":
        return { text: "Posting now", tone: "plain" };
      case "failed":
        return { text: "Didn’t post", tone: "alert" };
      case "connect":
        return { text: `Connect ${platformWord(data.channels.find((channel) => channel.id === post?.channelId)?.platform ?? "")}`, tone: "accent" };
      default:
        return { text: when ?? "No time yet", tone: "plain" };
    }
  };

  const rowActions = (channel: LiveChannel, post: LivePost | undefined) => {
    const route = post?.route;
    const items: Array<{ id: string; label: string }> = [];
    if (!post || post.liveSince || !canEdit) return items;
    if (route?.state === "connect" && stage.connect) items.push({ id: "connect", label: `Connect ${platformWord(channel.platform)}` });
    if (route?.state === "not-scheduled" && route.canSchedule && stage.schedule) items.push({ id: "schedule", label: `Schedule${route.at ? ` for ${whenOf(route, null, timeZone)}` : ""}` });
    if (route?.state === "scheduled" && stage.unschedule) items.push({ id: "unschedule", label: "Unschedule" });
    if (route?.state === "failed" && stage.retryPost) items.push({ id: "retry", label: "Try again" });
    if ((route?.state === "not-scheduled" || route?.state === "scheduled" || route?.state === "failed") && route.canSchedule !== false && stage.postNow)
      items.push({ id: "now", label: "Post now" });
    if (post.publicationId && route?.state !== "manual" && route?.state !== "posting" && stage.setSelfPost) items.push({ id: "self", label: "Post it yourself" });
    if (route?.selfPost && stage.setSelfPost) items.push({ id: "creo", label: "Let Creo post it" });
    if ((route?.state === "manual" || route?.state === "failed") && stage.setPostLink) items.push({ id: "posted", label: "Mark as posted" });
    return items;
  };
  const runRow = (channel: LiveChannel, key: Key) => {
    const id = String(key);
    const when = whenOf(postOf(channel.id)?.route, plannedAt, timeZone);
    // The row says what it is becoming at once and holds its menu until the
    // change lands (each change is a request, then a fresh read).
    const run = (becoming: string, action: (() => unknown) | undefined) => {
      setRowPending((all) => ({ ...all, [channel.id]: becoming }));
      void Promise.resolve(action?.())
        .catch(() => undefined)
        .finally(() =>
          setRowPending((all) => {
            const rest = { ...all };
            delete rest[channel.id];
            return rest;
          }),
        );
    };
    if (id === "connect") stage.connect?.(channel.id);
    else if (id === "schedule") run(when ? `Scheduled · ${when}` : "Scheduled", () => stage.schedule?.(channel.id));
    else if (id === "unschedule") run(when ?? "Not scheduled", () => stage.unschedule?.(channel.id));
    else if (id === "retry") stage.retryPost?.(channel.id);
    else if (id === "now") run("Posting now", () => stage.postNow?.(channel.id));
    else if (id === "self") run("Post it yourself", () => stage.setSelfPost?.(channel.id, true));
    else if (id === "creo") run(when ?? "No time yet", () => stage.setSelfPost?.(channel.id, false));
    else if (id === "posted") setLinkFor(channel.id);
  };

  const selfPanel = (channel: LiveChannel, post: LivePost, file: LiveFile | null) => {
    const due = post.route?.at ? new Date(post.route.at).getTime() <= now : false;
    const caption = post.caption.trim();
    const at = post.route?.at ? clockLabel(post.route.at, timeZone) : null;
    const name = platformWord(channel.platform);
    const copied = copiedFor === channel.id;
    const saveWord = `Save the ${file?.kind === "VIDEO" ? "video" : "picture"}`;
    const save = file?.mediaUrl ? (
      <a href={file.mediaUrl} download={file.name} target="_blank" rel="noreferrer" className={phone ? PHONE_WIDE : DESK_PILL}>
        {phone ? saveWord : "Download"}
        <Download className="size-3.5" aria-hidden="true" />
      </a>
    ) : null;
    const copy = caption ? (
      <button type="button" onClick={() => copyCaption(channel, caption)} className={phone ? PHONE_WIDE : DESK_PILL}>
        {copied ? "Copied" : "Copy caption"}
        {copied ? <Check className="size-3.5" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
      </button>
    ) : null;
    const fileMeta = file ? (
      <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-card bg-co-tonal p-2">
        <FileStill file={file} height={36} className="rounded-[6px]" />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[13px] text-co-text">{file.name}</span>
          <span className="truncate text-[12px] text-co-muted">
            {[file.kind === "VIDEO" ? "Video" : "Picture", file.ratio, durationLabel(file.durationSeconds), sizeLabel(file.sizeBytes)].filter(Boolean).join(" · ")}
          </span>
        </span>
      </div>
    ) : null;
    return (
      <div className="mb-3 flex flex-col gap-3 rounded-card border border-co-line2 bg-co-recess p-4">
        <p className="text-[14px] font-medium text-co-text">
          Yours to post on {name}.{at ? ` Creo ${due ? "reminded" : "reminds"} you at ${at}.` : ""}
        </p>
        {phone ? (
          // Figma 08 (13 Oct): Save the video and Copy caption as full-width 48 buttons with the
          // caption between them; Mark as posted stays the one primary, in the bottom bar.
          <>
            {fileMeta}
            {save}
            <div className="flex flex-col gap-1 rounded-card bg-co-tonal p-3">
              <p dir="auto" className="whitespace-pre-wrap text-[14px] leading-[1.45] text-co-text2">{caption || "No caption yet."}</p>
              <span className="text-[12.5px] text-co-muted">
                {name} caption · {caption.length} characters
              </span>
            </div>
            {copy}
            <p className="text-[12.5px] leading-[18px] text-co-muted">
              Post it{post.handle ? ` from ${post.handle}` : ""} on {name}, then mark it as posted. Creo starts the numbers from then.
            </p>
          </>
        ) : (
          <>
            {file ? (
              <div className="flex items-center gap-3">
                {fileMeta}
                {save}
              </div>
            ) : null}
            <div className="flex flex-col gap-2 rounded-card bg-co-tonal p-3">
              <p dir="auto" className="whitespace-pre-wrap text-[14px] leading-[1.45] text-co-text2">{caption || "No caption yet."}</p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12.5px] text-co-muted">
                  {name} caption · {caption.length} characters
                </span>
                {copy}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="min-w-0 flex-1 text-[12.5px] leading-[18px] text-co-muted">
                Post it{post.handle ? ` from ${post.handle}` : ""} on {name}, then mark it as posted. Creo starts the numbers from then.
              </p>
              {canEdit && stage.setPostLink ? (
                <KitButton onClick={() => setLinkFor(channel.id)}>Mark as posted</KitButton>
              ) : null}
            </div>
          </>
        )}
      </div>
    );
  };

  const channelRow = (channel: LiveChannel, index: number) => {
    const post = postOf(channel.id);
    const fileId = mainOf(channel.id);
    const file = fileId ? (byId.get(fileId) ?? null) : null;
    const earlier = data.channels.slice(0, index).find((other) => fileId && mainOf(other.id) === fileId);
    const cover = coverLine(channel);
    const coverPicture = coverOf(channel.id);
    const becoming = rowPending[channel.id] ?? null;
    const how: ReturnType<typeof howLabel> = becoming
      ? { text: becoming, tone: becoming === "Post it yourself" ? "accent" : "plain" }
      : howLabel(post);
    // Held while its own change or the post's How it posts switch is on its way.
    const held = !!becoming || !!switchingTo;
    const actions = rowActions(channel, post);
    const last = index === data.channels.length - 1;
    // Post it yourself: the file and caption to copy once its time comes (Figma 08, at 18:00).
    const self =
      post && !post.liveSince && post.route?.state === "manual" && canEdit && (!post.route.at || new Date(post.route.at).getTime() <= now);
    return (
      <li key={channel.id} className={cn(!last && "border-b border-co-line")}>
        <div className={cn("flex gap-3 py-3", phone ? "flex-wrap items-start" : "items-center")}>
          <PlatformChip platform={channel.platform} />
          <span className={cn("flex min-w-0 flex-col gap-0.5", phone ? "flex-1" : "w-[128px] shrink-0")}>
            <span className="truncate text-[13.5px] font-semibold leading-5 text-co-text2">{channelName(channel)}</span>
            <span className="truncate text-[12.5px] leading-[18px] text-co-muted">{post?.handle ?? ""}</span>
          </span>
          <span className={cn("flex min-w-0 items-center gap-2.5", phone ? "order-last basis-full pl-[38px]" : "flex-1")}>
            {/* The phone's one 64 × 80 tile with the file at its real shape (Emad's pick, 12 Oct); the desk's still at 48. */}
            {file ? (phone ? <FileStill file={file} height={80} box={[64, 80]} className="rounded-[8px]" /> : <FileStill file={file} height={48} className="rounded-[4px]" />) : null}
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-[12.5px] leading-[18px] text-co-text2">{file ? file.name : "No file yet"}</span>
              <span className="truncate text-[12.5px] leading-[18px] text-co-muted">
                {file
                  ? earlier
                    ? `Same file as ${channelName(earlier)}`
                    : [file.ratio, file.kind === "VIDEO" ? durationLabel(file.durationSeconds) : null].filter(Boolean).join(" · ")
                  : takesLine(channel)}
              </span>
              {file && cover ? (
                <span className="flex items-center gap-2">
                  {/* The cover is one line of text on the phone, no second small picture. */}
                  {coverPicture && !phone ? <FileStill file={byId.get(coverPicture) ?? null} height={36} className="rounded-[3px]" /> : null}
                  <span className="truncate text-[12.5px] leading-[18px] text-co-muted">{cover.replace(/^Cover: /, "Cover: ")}</span>
                  {canChangeCover(channel) ? (
                    <button type="button" onClick={() => setCoverFor(channel.id)} className={cn(LINK, "shrink-0 text-[12.5px]")}>
                      Change cover
                    </button>
                  ) : null}
                </span>
              ) : null}
            </span>
          </span>
          {actions.length ? (
            <MenuTrigger>
              <AriaButton
                aria-label={`How ${channelName(channel)} posts: ${how.text}`}
                aria-busy={!!becoming}
                isDisabled={held}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1 rounded-pill px-1 text-[12.5px] font-medium outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent disabled:cursor-default disabled:hover:text-inherit",
                  how.tone === "accent" ? "text-co-accent-ink" : how.tone === "alert" ? "text-co-alert-ink" : how.tone === "live" ? "text-co-muted" : "text-co-text2",
                )}
              >
                {how.text}
                {becoming ? (
                  <Loader2 className="size-3.5 animate-spin text-co-muted" aria-hidden="true" />
                ) : (
                  <ChevronDown className={cn("size-3.5 text-co-muted", held && "opacity-50")} aria-hidden="true" />
                )}
              </AriaButton>
              <KitPopover placement="bottom end" className="w-[220px] p-1.5">
                <Menu aria-label={`${channelName(channel)} posting`} className="flex flex-col outline-none" onAction={(key) => runRow(channel, key)}>
                  {actions.map((action) => (
                    <MenuItem
                      key={action.id}
                      id={action.id}
                      textValue={action.label}
                      className="flex min-h-9 cursor-default items-center rounded-row px-2.5 text-[13px] font-medium text-co-text2 outline-none data-[focused]:bg-co-hover data-[focused]:text-co-text"
                    >
                      {action.label}
                    </MenuItem>
                  ))}
                </Menu>
              </KitPopover>
            </MenuTrigger>
          ) : (
            <span className={cn("shrink-0 text-[12.5px] font-medium", how.tone === "live" ? "text-co-muted" : how.tone === "alert" ? "text-co-alert-ink" : "text-co-text2")}>{how.text}</span>
          )}
        </div>
        {self && post ? selfPanel(channel, post, file) : null}
      </li>
    );
  };

  const whereSection = (
    <section aria-labelledby="live-where" className="flex min-w-0 flex-col gap-3">
      <h2 id="live-where" className={LABEL}>
        Where it posts · {count} {count === 1 ? "channel" : "channels"}
      </h2>
      <ul className="m-0 list-none rounded-card bg-co-surface px-4 py-1">{data.channels.map(channelRow)}</ul>
    </section>
  );

  // ------------------------------------------------------------ captions
  const captionsEditable = canEdit && !!stage.saveCaption;
  const captionsSection = (
    <section aria-labelledby="live-captions" className="flex min-w-0 flex-col gap-2">
      <h2 id="live-captions" className={LABEL}>
        Captions · {count} {count === 1 ? "channel" : "channels"}, read in one go
      </h2>
      <ul className="m-0 grid list-none grid-cols-1 gap-x-5 gap-y-4 p-0 sm:grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
        {data.channels.map((channel) => {
          const post = postOf(channel.id);
          const caption = (post?.caption ?? "").trim();
          const editing = editingCaption?.channelId === channel.id;
          const when = whenOf(post?.route, plannedAt, timeZone);
          return (
            <li key={channel.id} className="flex min-w-0 flex-col gap-2 border-t border-co-line pt-3">
              <p className="text-[13.5px] font-semibold leading-5 text-co-text2">{channelName(channel)}</p>
              <p className="text-[12.5px] leading-[18px] text-co-muted">{[post?.handle, post?.liveSince ? `posted ${clockLabel(post.liveSince, timeZone)}` : when].filter(Boolean).join(" · ")}</p>
              {editing ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    value={editingCaption.text}
                    onChange={(event) => setEditingCaption({ channelId: channel.id, text: event.target.value })}
                    rows={5}
                    dir="auto"
                    aria-label={`${channelName(channel)} caption`}
                    className="w-full resize-y rounded-card bg-co-tonal px-3 py-2.5 text-[14px] leading-5 text-co-text outline-none focus:shadow-[inset_0_0_0_1px_var(--co-accent)]"
                  />
                  <div className="flex items-center gap-2">
                    <span className="mr-auto text-[12.5px] text-co-muted">{editingCaption.text.trim().length} characters</span>
                    <KitButton variant="ghost" onClick={() => setEditingCaption(null)}>
                      Cancel
                    </KitButton>
                    <KitButton
                      onClick={() => {
                        const text = editingCaption.text;
                        setEditingCaption(null);
                        void Promise.resolve(stage.saveCaption?.(channel.id, text)).catch(() => setEditingCaption({ channelId: channel.id, text }));
                      }}
                    >
                      Save
                    </KitButton>
                  </div>
                </div>
              ) : (
                <>
                  <p dir="auto" className={cn("custom-scrollbar max-h-60 overflow-y-auto whitespace-pre-wrap text-[14px] leading-5", caption ? "text-co-text2" : "text-co-muted")}>
                    {caption || "No caption yet."}
                  </p>
                  <p className="text-[13px] font-medium text-co-accent-ink">
                    {caption.length} characters
                    {captionsEditable && !post?.liveSince ? (
                      <>
                        {" · "}
                        <button
                          type="button"
                          onClick={() => setEditingCaption({ channelId: channel.id, text: post?.caption ?? "" })}
                          className={LINK}
                        >
                          Edit
                        </button>
                      </>
                    ) : null}
                  </p>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );

  // ------------------------------------------------------------ dialogs
  const coverChannel = coverFor ? data.channels.find((channel) => channel.id === coverFor) : undefined;
  const coverVideoId = coverChannel ? mainOf(coverChannel.id) : null;
  const coverVideo = coverVideoId ? byId.get(coverVideoId) : undefined;
  const linkChannel = linkFor ? data.channels.find((channel) => channel.id === linkFor) : undefined;
  const nowTargets = pending.filter((post) => post.route && ["not-scheduled", "scheduled", "failed"].includes(post.route.state) && post.route.canSchedule !== false);

  return (
    <div className="co-live-drop relative flex flex-col gap-10" data-over={dropping || undefined} {...dropZone}>
      {desk ? (
        <div className="grid grid-cols-[minmax(0,560fr)_minmax(0,664fr)] gap-8">
          {filesSection}
          <div className="flex min-w-0 flex-col gap-3">
            {howSection}
            {whereSection}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {howSection}
          {whereSection}
          {filesSection}
        </div>
      )}
      {captionsSection}

      {coverChannel && coverVideo ? (
        <LiveCoverDialog
          channel={coverChannel}
          video={coverVideo}
          pictures={pictures}
          frameMs={postOf(coverChannel.id)?.coverTimestampMs ?? null}
          coverFileId={coverOf(coverChannel.id)}
          onConfirm={(choice) => confirmCover(coverChannel, choice)}
          onAddPicture={stage.addFiles ? (file) => stage.addFiles!([file]) : undefined}
          onClose={() => setCoverFor(null)}
        />
      ) : null}

      {postNowAsk ? (
        <KitModal
          isOpen
          onOpenChange={(open) => !open && setPostNowAsk(false)}
          title={`Post ${nowTargets.length === 1 ? "it" : `all ${nowTargets.length}`} now?`}
          description={`${nowTargets.map((post) => channelName(data.channels.find((channel) => channel.id === post.channelId)!)).join(", ")} ${
            nowTargets.length === 1 ? "goes" : "go"
          } out within a minute${plannedAt ? `, instead of at ${dayLabel(plannedAt, timeZone)}, ${timeValue}` : ""}.`}
          modalClassName="max-w-[460px]"
        >
          <div className="mt-2 flex justify-end gap-3">
            <KitButton variant="ghost" onClick={() => setPostNowAsk(false)}>
              Cancel
            </KitButton>
            <KitButton
              isDisabled={!nowTargets.length}
              onClick={() => {
                setPostNowAsk(false);
                postEveryNow(nowTargets);
              }}
            >
              Post now
            </KitButton>
          </div>
        </KitModal>
      ) : null}

      {linkChannel ? <MarkPostedDialog channel={linkChannel} clientName={clientName} onSave={(url) => stage.setPostLink?.(linkChannel.id, url)} onClose={() => setLinkFor(null)} /> : null}
    </div>
  );
}

/** Mark as posted: the link to the post, which marks it live and starts its numbers. */
function MarkPostedDialog({
  channel,
  onSave,
  onClose,
}: {
  channel: LiveChannel;
  clientName: string;
  onSave: (url: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const name = platformWord(channel.platform);
  const valid = /^https?:\/\/\S+$/i.test(url.trim());
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => !open && !saving && onClose()}
      title="Mark as posted"
      description={`Paste the link to the ${name} post. Creo marks it live and starts its numbers from then.`}
      modalClassName="max-w-[480px]"
    >
      <label className="sr-only" htmlFor="live-posted-link">
        The link to the {name} post
      </label>
      <input
        id="live-posted-link"
        value={url}
        autoFocus
        onChange={(event) => setUrl(event.target.value)}
        placeholder={`https://www.${channel.platform.toLowerCase()}.com/…`}
        className="h-11 w-full rounded-card bg-co-tonal px-4 text-[14px] text-co-text outline-none placeholder:text-co-muted focus:shadow-[inset_0_0_0_1px_var(--co-accent)]"
      />
      <div className="mt-5 flex justify-end gap-3">
        <KitButton variant="ghost" onClick={onClose} isDisabled={saving}>
          Cancel
        </KitButton>
        <KitButton
          isDisabled={!valid || saving}
          onClick={() => {
            setSaving(true);
            void Promise.resolve(onSave(url.trim())).then(
              () => onClose(),
              () => setSaving(false),
            );
          }}
        >
          Mark as posted
        </KitButton>
      </div>
    </KitModal>
  );
}
